import { useState, useEffect } from "react";
import { useRoute } from "wouter";
import { motion } from "framer-motion";
import { Star, CheckCircle, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetReviewContext, useSubmitReview } from "@/lib/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function ReviewPage() {
  const [match, params] = useRoute("/review/:token");
  const token = match && params ? (params as any).token : "";
  const { data, isLoading, error } = useGetReviewContext(token, {
    query: {
      enabled: !!token,
      retry: false
    }
  });
  
  const submitReview = useSubmitReview();
  const { toast } = useToast();

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (data?.data?.existingReview) {
      setRating(data.data.existingReview.rating);
      setComment(data.data.existingReview.comment || "");
    }
  }, [data]);

  const handleSubmit = () => {
    if (rating === 0) {
      toast({ variant: "destructive", title: "Rating required", description: "Please select a star rating." });
      return;
    }

    submitReview.mutate(
      { token, rating, comment },
      {
        onSuccess: () => {
          setSubmitted(true);
        },
        onError: (err: any) => {
          toast({ variant: "destructive", title: "Error", description: err.message || "Failed to submit review." });
        }
      }
    );
  };

  if (!token) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">Invalid Review Link</div>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm border text-center">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-foreground">Link Invalid or Expired</h2>
          <p className="text-muted-foreground mb-6">{(error as any).message || "This review link is no longer valid."}</p>
          <Link href="/">
            <Button className="w-full">Go to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  const reviewCtx = data?.data;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm border text-center"
        >
          <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-foreground">Thank You!</h2>
          <p className="text-muted-foreground mb-6">Your review for {reviewCtx?.providerName} has been submitted successfully.</p>
          <Link href="/">
            <Button variant="outline" className="w-full">Return Home</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-xl w-full">
        <Link href="/">
          <Button variant="ghost" size="sm" className="mb-6 -ml-2 text-muted-foreground">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </Link>
        
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-primary/80 p-8 text-center text-white">
            <h1 className="text-2xl font-bold mb-2">How was your visit?</h1>
            <p className="text-primary-foreground/80">Rate your experience with {reviewCtx?.providerName}</p>
          </div>
          
          <div className="p-8">
            <div className="flex justify-center mb-8">
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        star <= (hoverRating || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      } transition-colors`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Additional Comments (Optional)
                </label>
                <Textarea
                  placeholder="Share details of your experience..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-[120px] resize-none"
                />
              </div>

              {reviewCtx?.existingReview && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-800">You previously reviewed this appointment</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    Submitting again will update your existing review.
                  </AlertDescription>
                </Alert>
              )}

              <Button 
                className="w-full h-12 text-base mt-4" 
                onClick={handleSubmit}
                disabled={submitReview.isPending || rating === 0}
              >
                {submitReview.isPending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                {reviewCtx?.existingReview ? "Update Review" : "Submit Review"}
              </Button>
            </div>
          </div>
        </div>
        
        <p className="text-center text-xs text-muted-foreground mt-6">
          © {new Date().getFullYear()} StraightDoc. Your feedback helps improve care quality.
        </p>
      </div>
    </div>
  );
}
